/**
 * Catalog Issues List Component
 *
 * Displays SKU_NOT_FOUND issues — order lines whose SKU has no `skus` row for
 * the row-owning org, so the batch-time catalog guard skipped them entirely
 * (production-orders.service.ts `_createSkuNotFoundIssues`; previously these
 * issues rendered on no tab at all — a coordinator had no way to see them).
 * Blank-simple like RedoIssuesList: fetch + IssueCard, no bespoke grouping.
 */
import { useTranslation } from 'react-i18next';
import { PackageSearch } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useProductionIssues } from '../hooks/useProductionIssues';
import { IssueCard } from './IssueCard';

interface CatalogIssuesListProps {
  locationId: string;
}

export function CatalogIssuesList({ locationId }: CatalogIssuesListProps) {
  const { t } = useTranslation('production');
  const { data: issues, isLoading } = useProductionIssues(locationId, {
    status: 'OPEN',
    issueType: 'SKU_NOT_FOUND',
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2].map((i) => (
          <Skeleton key={i} className="h-40 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (!issues || issues.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <PackageSearch className="h-12 w-12 text-muted-foreground" aria-hidden="true" />
        <p className="mt-4 text-lg font-medium">{t('issues.catalog.noIssues')}</p>
        <p className="text-sm text-muted-foreground">
          {t('issues.catalog.willAppearHere')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {issues.map((issue) => (
        <IssueCard key={issue.id} issue={issue} />
      ))}
    </div>
  );
}
